/**
 * Reverses the header colours when the first visible hero on the page asks for it.
 *
 * This used to be emitted inline by sections/hero.liquid, which meant the whole
 * ~6KB manager was duplicated once per hero section — six times on the homepage,
 * parsed and executed on every load. It lives here now and registers itself by
 * scanning for [data-hero-header-colors], so the section only has to emit its
 * three settings as data attributes.
 */
(() => {
  const CLASS_DESKTOP = 'header-colors--reversed-desktop';
  const CLASS_MOBILE = 'header-colors--reversed-mobile';
  const SELECTOR = '[data-hero-header-colors]';

  const manager = {
    instances: [],
    eventsBound: false,

    getVisibleSections() {
      return Array.from(document.querySelectorAll('main .shopify-section'))
        .filter((el) => el.offsetParent !== null);
    },

    getFirstHeroInstance() {
      const visibleSections = this.getVisibleSections();

      for (const sectionElement of visibleSections) {
        const match = this.instances.find((instance) => instance.sectionElement === sectionElement);
        if (match) return match;
      }

      return null;
    },

    clearClasses() {
      document.body.classList.remove(CLASS_DESKTOP, CLASS_MOBILE);
    },

    // Both classes are safe to set at the same time: the stylesheet scopes
    // each one to its own media query, so the viewport decides which wins.
    // Doing it here with innerWidth instead would drift from the CSS
    // breakpoint by the scrollbar width.
    apply() {
      const activeInstance = this.getFirstHeroInstance();

      this.clearClasses();

      if (!activeInstance) return;

      if (activeInstance.reverseDesktop) {
        document.body.classList.add(CLASS_DESKTOP);
      }

      if (activeInstance.reverseMobile) {
        document.body.classList.add(CLASS_MOBILE);
      }
    },

    watchHero(instance) {
      if (!instance.hero || instance.heroObserverInitialized) return;

      instance.heroObserverInitialized = true;

      const runApply = () => {
        const firstInstance = this.getFirstHeroInstance();

        if (firstInstance && firstInstance.sectionId === instance.sectionId) {
          this.apply();
        }
      };

      // The first hero no longer waits for AOS — it paints straight from HTML and
      // carries aos-animate from the start, so this resolves immediately for it.
      if (instance.hero.classList.contains('aos-animate')) {
        runApply();
        return;
      }

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'class' &&
            instance.hero.classList.contains('aos-animate')
          ) {
            observer.disconnect();
            runApply();
            break;
          }
        }
      });

      observer.observe(instance.hero, { attributes: true });
      instance.observer = observer;
    },

    rebind(instance) {
      instance.sectionElement = document.getElementById(`shopify-section-${instance.sectionId}`);
      instance.hero = instance.sectionElement?.querySelector('.hero') || null;
      instance.heroObserverInitialized = false;

      if (instance.observer) {
        instance.observer.disconnect();
        instance.observer = null;
      }

      this.watchHero(instance);
    },

    refresh() {
      this.instances = this.instances.filter((instance) => {
        if (!document.getElementById(`shopify-section-${instance.sectionId}`)) {
          if (instance.observer) instance.observer.disconnect();
          return false;
        }

        this.rebind(instance);
        return true;
      });

      this.scan();
      this.apply();
    },

    bindEvents() {
      if (this.eventsBound) return;
      this.eventsBound = true;

      window.addEventListener('resize', () => this.apply());

      document.addEventListener('shopify:section:load', () => {
        this.refresh();
      });

      document.addEventListener('shopify:section:select', () => {
        this.apply();
      });

      document.addEventListener('shopify:section:reorder', () => {
        this.refresh();
      });

      document.addEventListener('shopify:section:unload', () => {
        this.refresh();
      });
    },

    register(config) {
      const existing = this.instances.find((item) => item.sectionId === config.sectionId);

      // The theme editor re-renders the section on every setting change and
      // re-runs this script. Bailing out here would leave the old toggle
      // values in place, which is why the checkboxes only took effect after
      // a full page reload.
      if (existing) {
        existing.reverseDesktop = config.reverseDesktop;
        existing.reverseMobile = config.reverseMobile;

        this.rebind(existing);
        this.apply();
        return;
      }

      const instance = {
        sectionId: config.sectionId,
        reverseDesktop: config.reverseDesktop,
        reverseMobile: config.reverseMobile,
        sectionElement: document.getElementById(`shopify-section-${config.sectionId}`),
        hero: null,
        observer: null,
        heroObserverInitialized: false
      };

      instance.hero = instance.sectionElement?.querySelector('.hero') || null;

      this.instances.push(instance);
      this.bindEvents();
      this.watchHero(instance);
      this.apply();
    },

    // Registers in DOM order, which is what getFirstHeroInstance relies on.
    scan() {
      document.querySelectorAll(SELECTOR).forEach((el) => {
        const sectionId = el.getAttribute('data-section-id');
        if (!sectionId) return;

        this.register({
          sectionId,
          reverseDesktop: el.getAttribute('data-reverse-desktop') === 'true',
          reverseMobile: el.getAttribute('data-reverse-mobile') === 'true'
        });
      });
    }
  };

  window.__heroHeaderColorManager = manager;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => manager.scan());
  } else {
    manager.scan();
  }
})();
